import { useState, useEffect } from 'react';
import { Button, Card, Text, Group, CloseButton } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * PWA Install Prompt Component
 *
 * Shows a prompt to install the app as a PWA after 30 seconds.
 * Only appears if the app is not already installed and user hasn't dismissed it.
 */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed
    }

    // Check if user dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show prompt after 30 seconds (don't be annoying)
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <Card
      shadow="lg"
      padding="md"
      radius="md"
      withBorder
      style={{
        position: 'fixed',
        bottom: '80px', // Above bottom nav
        left: '16px',
        right: '16px',
        zIndex: 1000,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ flex: 1 }}>
          <Text fw={600} size="sm" mb={4}>
            Install Reading App
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Add to your home screen for quick access and offline reading
          </Text>
          <Button
            size="xs"
            leftSection={<IconDownload size={14} />}
            onClick={handleInstall}
          >
            Install
          </Button>
        </div>
        <CloseButton onClick={handleDismiss} size="sm" />
      </Group>
    </Card>
  );
}
